// import type { ProductFieldsFragment } from '$lib/types/storefront.generated';
// import type { ProductVariantFieldsFragment } from '$lib/types/storefront.generated';
import type { GetCollectionProductsQuery } from '$lib/types/storefront.generated';
import type { LanguageCode } from '$lib/types/storefront.types';
import { getLocale } from '$lib/paraglide/runtime';
import siteCollections from '$lib/config/site-collections.json';

export type CollectionData = NonNullable<GetCollectionProductsQuery['collection']>;
export type ProductNode = CollectionData['products']['edges'][0]['node'];
export type ProductVariantNode = ProductNode['variants']['edges'][0]['node'];

export const COLLECTION_ID = siteCollections.shoes;
export const MATS_COLLECTION_ID = siteCollections.mats;
export const PRODUCT_TYPE = "SHOES";

/**
 * Map locale to Shopify LanguageCode for @inContext directive.
 * Uses getLocale() from paraglide which works on both server and client.
 */
export function getShopifyLanguage(): LanguageCode {
  const locale = getLocale();
  const map: Record<string, string> = { en: 'EN', el: 'EL', ru: 'RU', he: 'HE' };
  return (map[locale] || 'EN') as LanguageCode;
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')    // collapse everything that is not a–z/0–9
    .replace(/^-|-$/g, '');         // trim leading/trailing dashes
}

export function getShopifyImageUrl(url: string, width: number, format?: string) {
    if (!url) return '';

    try {
      const urlObj = new URL(url);

      // Remove existing image transformation parameters
      ['width', 'height', 'crop', 'format'].forEach(param => {
        urlObj.searchParams.delete(param);
      });

      // Add width only - no height/crop to preserve full image
      urlObj.searchParams.set('width', width.toString());
      if (format) {
        urlObj.searchParams.set('format', format);
      }

      return urlObj.toString();
    } catch (e) {
      console.error('Error transforming image URL:', e);
      return url;
    }
  }

export function getResponsiveImageSrcSet(url: string, baseWidth: number) {
    if (!url) return '';

    const sizes = [
      { width: baseWidth, density: '1x' },
      { width: Math.round(baseWidth * 1.5), density: '1.5x' },
      { width: baseWidth * 2, density: '2x' },
    ];

    return sizes
      .map(({ width, density }) => `${getShopifyImageUrl(url, width, 'webp')} ${density}`)
      .join(', ');
}

/**
 * Build a width-descriptor srcset: 172w, 344w, 516w …
 */
export function getSrcSetByWidths(
  url: string,
  widths: number[],
  format: 'avif' | 'webp' | 'jpg' = 'webp'
) {
  return widths
    .map((w) => `${getShopifyImageUrl(url, w, format)} ${w}w`)
    .join(', ');
}
  

// Format price helper
 export function formatPrice(price: { amount: string; currencyCode: string }) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: price.currencyCode,
    }).format(parseFloat(price.amount));
 }
  
 export function formatTitleCase(input: string): string {
    return input
      .split(/\s+/) // split by whitespace
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Title case
      .join(' ');
  }
 export function formatTitle(input: string): string {
  // Words to remove (case-insensitive)
  const wordsToRemove = ['shoe', 'shoes', 'men', 'mens', 'women', 'womens'];

  return input
    .split(/\s+/) // split by whitespace
    .filter(word => !wordsToRemove.includes(word.toLowerCase())) // remove unwanted words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Title case
    .join(' ');
}


  // Check if there's a discount
 export function hasDiscount(variant: any) {
    if (!variant.compareAtPrice || !variant.price) return false;
    return parseFloat(variant.compareAtPrice.amount) > parseFloat(variant.price.amount);
  }

  // Get first available variant
export  function getFirstVariant(product: ProductNode) {
    return product.variants.edges[0]?.node;
  }

  // Get first image
export  function getFirstImage(product: ProductNode) {
    return product.images.edges[0]?.node;
  }


/** utility: find variant by option value */
export function variantFor(product: ProductNode, optionName: string, value: string) {
    return product.variants.edges.find(e =>
      e.node.selectedOptions.some(o => o.name === optionName && o.value === value)
    )?.node;
  }
